import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, filter, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { ColorModeService } from '../../services/color-mode.service';
import { AuthActions, AuthApiActions } from '../auth/auth.actions';
import { ColorModeActions, UiActions } from './ui.actions';

@Injectable()
export class UiEffects {
  private readonly actions$ = inject(Actions);
  private readonly colorModeService = inject(ColorModeService);

  readonly persistLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UiActions.languageSelected),
        tap(({ language }) => localStorage.setItem('preferred_language', language)),
      ),
    { dispatch: false },
  );

  readonly loadColorModeAfterAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.loginSucceeded, AuthActions.appRehydrated),
      map(() => ColorModeActions.loadRequested()),
    ),
  );

  readonly loadColorMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColorModeActions.loadRequested),
      switchMap(() =>
        this.colorModeService.getColorMode().pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(AuthActions.logoutRequested, ColorModeActions.selected),
            ),
          ),
          map(() => ColorModeActions.loadSucceeded({ mode: 'light' })),
          catchError(() => of(ColorModeActions.loadFailed())),
        ),
      ),
    ),
  );

  readonly saveColorMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ColorModeActions.selected),
      filter(({ mode }) => mode === 'light'),
      concatMap(({ mode }) =>
        this.colorModeService.saveColorMode(mode).pipe(
          map(() => ColorModeActions.saveSucceeded({ mode })),
          catchError(() => of(ColorModeActions.saveFailed({ mode }))),
        ),
      ),
    ),
  );
}
